
import Vue, { PropType } from 'vue';

// Services and types
import CandidateService from '~/services/CandidateService';
import DialogService from '~/services/DialogService';
import { CandidateJobBuilderChangeEvent, commonProps, setupBuilder } from '~/utils/candidate';
import { CandidateJobBuilder } from '~/utils/candidate/CandidateJobBuilder';
import { ImportSection } from '~/services/types/import';
import IdentityService from '~/services/IdentityService';

// Components
import InfoBox from '../components/InfoBox.vue';
import PersonalOverviewForm from '../forms/PersonalOverviewForm.vue';
import PersonalPreferencesForm from '../forms/PersonalPreferencesForm.vue';
import ProfileModal from './ProfileModal.vue';
import ImportBox from '~/components/profile/components/ImportBox.vue';

export default Vue.extend({
    name: 'PersonalDetailsModal',
    components: {
        ProfileModal,
        InfoBox,
        PersonalOverviewForm,
        PersonalPreferencesForm,
        ImportBox,
    },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        importSection: {
            type: Object as PropType<ImportSection | undefined>,
            required: false,
            default: undefined,
        },

        ...commonProps(),
    },
    data() {
        return {
            builder: null as CandidateJobBuilder | null,
            service: new CandidateService(),
            identityService: new IdentityService(),

            newAvatar: null as File | null,
            isLoading: false,

            hasUnsavedChanges: false,
            isFormValid: false,
        };
    },
    methods: {
        handleBuilderChange(evt: CandidateJobBuilderChangeEvent) {
            this.hasUnsavedChanges = !evt.empty || !!this.newAvatar;
        },
        handleAvatarChange(file: File) {
            this.hasUnsavedChanges = true;
            this.newAvatar = file;
        },
        async handleSave() {
            if (!this.builder || (this.builder.isEmpty() && !this.newAvatar)) return;

            this.isLoading = true;
            let avatarPromise = null;

            if (!!this.newAvatar) {
                avatarPromise = this.identityService.saveImage(this.newAvatar);
            }

            const request = this.builder.buildRequest();
            const profilePromise = this.service.updateCandidateProfile(request, false);

            await Promise.all([
                avatarPromise?.catch(error => error) ?? null,
                profilePromise.catch(error => error),
            ])
                .then(values => {
                    const avatarError = values[0];
                    this.$emit('close');

                    if (avatarError !== null && Boolean(avatarError) !== true) {
                        this.showImageUploadError();
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                    this.service.dispatchProfileUpdated(request);
                });

            this.newAvatar = null;
            this.builder.reset();
        },
        handleClose() {
            this.$emit('close');
        },
        showImageUploadError() {
            const closeAction = ({ res }: { res: (s: string) => void }) => {
                res('close');
            };

            DialogService.openDialog({
                title: this.$t('spotlight.errors.imageUpload.title').toString(),
                content: this.$t('profile.personalDetails.errors.imageUploadFail').toString(),
                isError: true,
                actions: [
                    {
                        title: this.$t('general.ok'),
                        variant: 'primary',
                        action: closeAction,
                    },
                ],
            });
        },
    },
    mounted() {
        const builder = setupBuilder.apply(this);
        builder.on('change', this.handleBuilderChange);
    },
    beforeDestroy() {
        if (this.builder) this.builder.off('change', this.handleBuilderChange);
    },
});
